import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';
import api from '../services/api';
import useToastStore from '../store/useToastStore';

function SkeletonCard() {
  return (
    <div className="bg-dark-surface border border-dark-border rounded-2xl p-4 flex gap-3 animate-pulse">
      <div className="w-20 h-20 rounded-xl bg-dark-muted flex-shrink-0" />
      <div className="flex-1">
        <div className="h-4 bg-dark-muted rounded-lg w-3/4 mb-2" />
        <div className="h-3 bg-dark-muted rounded-lg w-1/2 mb-3" />
        <div className="flex gap-2">
          <div className="h-8 bg-dark-muted rounded-xl flex-1" />
          <div className="h-8 bg-dark-muted rounded-xl flex-1" />
        </div>
      </div>
    </div>
  );
}

export default function MyServicesPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currency } = useCurrencyStore();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/services?providerId=${user?.id}`);
      const list = Array.isArray(data) ? data : (data.services || []);
      // Filter client-side in case backend ignores the query param
      const mine = list.filter((s) => s.provider?.id === user?.id || s.providerId === user?.id);
      setServices(mine.length > 0 ? mine : list);
    } catch {
      setServices([]);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Delete this listing? This action cannot be undone.');
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await api.delete(`/services/${id}`);
      setServices((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      useToastStore.getState().addToast(err.response?.data?.error || 'Failed to delete listing.', 'error');
    }
    setDeletingId(null);
  };

  const handleToggleActive = async (service) => {
    setTogglingId(service.id);
    try {
      const { data } = await api.patch(`/services/${service.id}/toggle-active`);
      setServices((prev) =>
        prev.map((s) => s.id === service.id ? { ...s, isActive: data.isActive } : s)
      );
    } catch {
      useToastStore.getState().addToast('Failed to toggle listing', 'error');
    }
    setTogglingId(null);
  };

  // Guard: only providers
  if (user?.accountType !== 'PROVIDER' && user?.accountType !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-dark-bg flex flex-col items-center justify-center px-6 pb-24 pt-12">
        <p className="text-4xl mb-3">🔒</p>
        <p className="text-dark-text font-semibold text-lg mb-1">Providers only</p>
        <p className="text-dark-subtext text-sm text-center">This page is only available for provider accounts.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 px-6 py-3 rounded-2xl bg-brand-gradient text-white font-semibold text-sm"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>My Listings</h1>
        <button
          onClick={() => navigate('/create-service')}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-brand-gradient text-white"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      <div className="px-4">
        {loading ? (
          <div className="flex flex-col gap-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : services.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-20 pb-10">
            <div className="w-20 h-20 rounded-3xl bg-dark-surface border border-dark-border flex items-center justify-center text-4xl mb-4">
              🗂️
            </div>
            <p className="text-dark-text font-semibold text-lg mb-1">No listings yet</p>
            <p className="text-dark-subtext text-sm text-center mb-6">
              Create your first listing to start receiving bookings.
            </p>
            <button
              onClick={() => navigate('/create-service')}
              className="px-8 py-3.5 rounded-2xl bg-brand-gradient text-white font-bold text-sm"
            >
              Create Your First Listing
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {services.map((service) => (
              <div
                key={service.id}
                className={`bg-dark-surface border rounded-2xl p-4 transition-opacity ${
                  service.isActive === false ? 'opacity-60 border-dark-border' : 'border-dark-border'
                }`}
              >
                <div className="flex gap-3">
                  {/* Thumbnail */}
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-dark-muted flex-shrink-0">
                    {service.images?.[0] ? (
                      <img src={service.images[0]} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <div className="w-full h-full bg-brand-gradient opacity-50 flex items-center justify-center text-2xl">
                        🎨
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <p className="text-dark-text text-sm font-semibold leading-snug line-clamp-2 flex-1">
                        {service.title}
                      </p>
                      {/* Active toggle — circle RIGHT = active, circle LEFT = inactive */}
                      <button
                        onClick={() => handleToggleActive(service)}
                        disabled={togglingId === service.id}
                        className={`relative flex-shrink-0 w-12 h-7 rounded-full transition-colors duration-300 ${
                          service.isActive === true ? 'bg-brand-purple' : 'bg-dark-muted'
                        } ${togglingId === service.id ? 'opacity-50' : ''}`}
                      >
                        <span
                          className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300"
                          style={{ transform: service.isActive === true ? 'translateX(20px)' : 'translateX(0)' }}
                        />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-brand-purple text-sm font-bold">
                        {service.listingType === 'STUDIO'
                          ? `${formatPrice(service.hourlyRate || service.price || 0, currency)}/hr`
                          : formatPrice(service.price || 0, currency)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        service.isActive === true
                          ? 'text-green-400 bg-green-400/10'
                          : 'text-dark-subtext bg-dark-muted'
                      }`}>
                        {service.isActive === true ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/edit-service/${service.id}`)}
                        className="flex-1 py-2 rounded-xl bg-dark-bg border border-dark-border text-dark-text text-xs font-semibold flex items-center justify-center gap-1"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        disabled={deletingId === service.id}
                        className="flex-1 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
                      >
                        {deletingId === service.id ? (
                          <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                            </svg>
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
